import React from "react";
import { Dropdown } from "primereact/dropdown";
import type { MonthKey, PeriodRange } from "../../../../types/targetGroupAnalytics";
import { monthKeyToString, monthLabel, parseMonthKey } from "../utils/format";

interface MonthRangeSelectorProps {
  value: PeriodRange;
  onChange: (period: PeriodRange) => void;
  /** How many months back to offer (default 24). */
  monthsBack?: number;
}

const lastNMonths = (n: number): MonthKey[] => {
  const now = new Date();
  const out: MonthKey[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
  }
  return out;
};

/** Month-range selector (FR-011). The app's PeriodSelector is week-based, so
 *  this is a dedicated month version that keeps from <= to. */
const MonthRangeSelector: React.FC<MonthRangeSelectorProps> = ({ value, onChange, monthsBack = 24 }) => {
  const options = lastNMonths(monthsBack).map((m) => ({
    label: monthLabel(m),
    value: monthKeyToString(m),
  }));

  const fromStr = monthKeyToString(value.from);
  const toStr = monthKeyToString(value.to);

  const handleFrom = (s: string) => {
    const from = parseMonthKey(s);
    // keep from <= to (string compare works on zero-padded YYYY-MM)
    const to = toStr < s ? from : value.to;
    onChange({ from, to });
  };

  const handleTo = (s: string) => {
    const to = parseMonthKey(s);
    const from = s < fromStr ? to : value.from;
    onChange({ from, to });
  };

  return (
    <div className="flex gap-2 align-items-center" data-testid="month-range-selector">
      <span className="text-xs text-color-secondary">Period:</span>
      <Dropdown
        value={fromStr}
        options={options}
        onChange={(e) => handleFrom(e.value)}
        style={{ width: "8.5rem" }}
        aria-label="From month"
      />
      <span className="text-xs text-color-secondary">to</span>
      <Dropdown
        value={toStr}
        options={options}
        onChange={(e) => handleTo(e.value)}
        style={{ width: "8.5rem" }}
        aria-label="To month"
      />
    </div>
  );
};

export default MonthRangeSelector;
